/**
 * ChatComposer - bottom composer bar for the AI chat pane.
 *
 * Layout:
 *   ┌───────────────────────────────────────────────────┐
 *   │ [SelectedContextBar - explicit context chips]     │
 *   ├───────────────────────────────────────────────────┤
 *   │ [Textarea - auto-expanding prompt input]          │
 *   ├───────────────────────────────────────────────────┤
 *   │ [ModelSelect]                     [Stop | Send ▶] │
 *   └───────────────────────────────────────────────────┘
 *
 * Keyboard: Enter to send, Shift+Enter for newline.
 * When a run is active: Send becomes Stop (sends cancel_run).
 *
 * Context model:
 *   The SelectedContextBar renders `explicitContextItems` - typed context items
 *   explicitly selected by the user from project menu surfaces (repo tree,
 *   Monaco line selection, and future: files, docs, boards, pipelines).
 *   Each chip is individually removable. A "Clear all" appears when >1 items.
 */

import type {
  ChatMode,
  ExplicitContextItem,
  GeminiModel,
  SlashCommandDefinition,
} from '@protoss/shared';
import {
  CONTEXT_COMPACT_SUGGEST_RATIO,
  CONTEXT_DANGER_RATIO,
  CONTEXT_DONUT_MIN_RATIO,
  SLASH_COMMANDS,
  SLASH_COMMAND_CATEGORIES,
  isCommandAvailableAtStage,
  isStageAvailable,
} from '@protoss/shared';
import { Popover, Tooltip, UsageDonut } from '@protoss/ui';
import * as React from 'react';
import { useWorkspace } from '../../../contexts/WorkspaceContext';
import { useWorkspaceAdmin } from '../../../hooks/useWorkspaceAdmin';
import type { SendRunOptions } from '../hooks/useChatSession';
import type { ChatConnectionStatus } from '../state/chatState';
import { AiDisclosurePanel } from './AiDisclosurePanel';
import { ChatConnectionIndicator } from './ChatConnectionIndicator';
import { CHAT_MODE_OPTIONS } from './ChatModeToggle';
import { ComposerPicker, type ComposerPickerOption } from './ComposerPicker';
import { SelectedContextBar } from './SelectedContextBar';
import { SlashCommandMenu } from './SlashCommandMenu';

export interface ChatComposerProps {
  /** Explicit context items from project menu interactions to show as chips. */
  explicitContextItems: ExplicitContextItem[];
  /** Called when the user clicks × on a chip to remove that item. */
  onRemoveExplicitContext: (itemId: string) => void;
  /** Called when the user clicks "Clear all" to dismiss all chips at once. */
  onClearExplicitContext: () => void;
  /** Pending (unconfirmed) draft context item, shown with distinct styling. */
  draftExplicitItem?: ExplicitContextItem | null;
  /** Called when user clicks the draft chip body to confirm it. */
  onConfirmDraftExplicitContext?: () => void;
  /** Called when user clicks × on the draft chip to dismiss it. */
  onClearDraftExplicitContext?: () => void;
  isRunActive: boolean;
  isConnected: boolean;
  /** Focus the textarea on mount (landing-page centered chat). */
  autoFocus?: boolean;
  /** Selected ChatMode (Ask/Agent/Auto). Falls back to internal state when omitted. */
  mode?: ChatMode;
  onModeChange?: (mode: ChatMode) => void;
  onSend: (opts: SendRunOptions) => void;
  onStop: () => void;
  /**
   * A mid-run question is open in the UserInteractionDrawer above. The composer
   * stays mounted but send-disabled - the drawer owns the answer path.
   */
  promptPending?: boolean;
  /**
   * Opt-in inline connection indicator, rendered left of the Stop/Send button.
   * For surfaces without a pane header to host ChatConnectionIndicator (e.g.
   * the landing hero composer). Provide all three or none.
   */
  connectionStatus?: ChatConnectionStatus;
  reconnectAttempt?: number;
  onReconnect?: () => void;
  /**
   * [Context budget] Fraction (0–1) of the model context window the session
   * currently occupies (last run's promptTokens / window). Null/undefined hides
   * the indicator. At CONTEXT_COMPACT_SUGGEST_RATIO the chip turns warning and
   * a one-click Compact button appears - mirrors Claude Code.
   */
  contextUsageRatio?: number | null;
}

export function ChatComposer({
  explicitContextItems,
  onRemoveExplicitContext,
  onClearExplicitContext,
  draftExplicitItem,
  onConfirmDraftExplicitContext,
  onClearDraftExplicitContext,
  isRunActive,
  isConnected,
  autoFocus,
  mode: modeProp,
  onModeChange,
  onSend,
  onStop,
  promptPending,
  connectionStatus,
  reconnectAttempt,
  onReconnect,
  contextUsageRatio,
}: ChatComposerProps) {
  const { workspace } = useWorkspace();
  // Stage-gate model tiers (the thinking tier is 'alpha' = local/dev only) and chat-mode
  // options (Agent/Auto are 'alpha') against the workspace min stage. See isStageAvailable.
  const featureMinStage = workspace?.featureMinStage;
  const availableModels = React.useMemo(
    () => (workspace?.aiModels ?? []).filter((m) => isStageAvailable(m.stage, featureMinStage)),
    [workspace?.aiModels, featureMinStage]
  );
  const [prompt, setPrompt] = React.useState('');
  const [focused, setFocused] = React.useState(false);
  // Mac keyboards label the key "Return", not "Enter".
  const enterKey = React.useMemo(
    () =>
      /Mac|iP(hone|ad|od)/.test(
        typeof navigator !== 'undefined' ? navigator.platform || navigator.userAgent : ''
      )
        ? 'Return'
        : 'Enter',
    []
  );
  const [model, setModel] = React.useState<GeminiModel>(() => availableModels[0]?.id ?? '');
  // Mode: controlled when the page provides it (shared composerMode), else local.
  const [localMode, setLocalMode] = React.useState<ChatMode>('ask');
  const mode = modeProp ?? localMode;
  const handleModeChange = (m2: ChatMode) => {
    if (onModeChange) onModeChange(m2);
    else setLocalMode(m2);
  };

  // Sync selected model when the available model list loads
  React.useEffect(() => {
    if (availableModels.length > 0 && !availableModels.some((m) => m.id === model)) {
      setModel(availableModels[0]!.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableModels]);
  const modelOptions = React.useMemo<ComposerPickerOption<GeminiModel>[]>(
    () =>
      availableModels.map((m) => ({
        value: m.id,
        label: m.label,
        ...(m.description ? { description: m.description } : {}),
        ...(m.stage === 'alpha' || m.stage === 'beta' ? { stage: m.stage } : {}),
      })),
    [availableModels]
  );
  const modeOptions = React.useMemo(
    () => CHAT_MODE_OPTIONS.filter((o) => isStageAvailable(o.stage, featureMinStage)),
    [featureMinStage]
  );
  // Label of the selected model, for the "How AI works here" disclosure panel
  // (same source RunStatusStrip uses for its model attribution).
  const selectedModelLabel = React.useMemo(
    () => availableModels.find((m) => m.id === model)?.label,
    [availableModels, model]
  );
  const textareaRef = React.useRef<HTMLTextAreaElement>(null);

  // Landing-page centered chat: focus the composer on mount.
  React.useEffect(() => {
    if (autoFocus) textareaRef.current?.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-resize textarea as the user types.
  // CSS max-height (14rem / ~10 lines) caps the growth; overflow-y: auto
  // only shows a scrollbar once content exceeds that cap.
  const adjustHeight = React.useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, []);

  // Run once on mount so the initial single-row height is correct.
  React.useEffect(() => {
    adjustHeight();
  }, [adjustHeight]);

  // ── Slash command menu ──────────────────────────────────────────────────────
  // Trigger rules (escape hatch for genuine '/' text):
  //  - Opens ONLY when '/' is typed as the first character of an EMPTY composer
  //    (or via the toolbar button). '/' anywhere else never triggers.
  //  - Escape closes and keeps the '/' as plain text (menuDismissed suppresses
  //    re-opening until the composer empties again).
  //  - Typing a space after the slash dismisses (it's a real message).
  const isAdmin = useWorkspaceAdmin(workspace);
  // Sorted by category so the menu's group headers line up with the flat
  // activeIndex keyboard navigation (filter below preserves this order).
  const availableCommands = React.useMemo(() => {
    const categoryRank = (c: SlashCommandDefinition) =>
      SLASH_COMMAND_CATEGORIES.findIndex((g) => g.key === c.category);
    return SLASH_COMMANDS.filter(
      (c) =>
        (!c.requiresWorkspaceAdmin || isAdmin) &&
        isCommandAvailableAtStage(c, workspace?.featureMinStage)
    )
      .slice()
      .sort((a, b) => categoryRank(a) - categoryRank(b));
  }, [isAdmin, workspace?.featureMinStage]);
  const [menuOpen, setMenuOpen] = React.useState(false);
  const [menuDismissed, setMenuDismissed] = React.useState(false);
  const [activeCommandIndex, setActiveCommandIndex] = React.useState(0);

  const commandFilter = prompt.startsWith('/') ? prompt.slice(1) : '';
  const filteredCommands = React.useMemo(
    () =>
      availableCommands.filter((c) => c.key.toLowerCase().startsWith(commandFilter.toLowerCase())),
    [availableCommands, commandFilter]
  );

  const closeMenu = React.useCallback((dismissed: boolean) => {
    setMenuOpen(false);
    setActiveCommandIndex(0);
    if (dismissed) setMenuDismissed(true);
  }, []);

  // Selecting a command FILLS the composer (it never auto-sends): the user can
  // add a scoped request after the label ("/init-workspace just the logos") and
  // press Enter. handleSend recognises the leading /command and sends commandKey.
  const handleSelectCommand = React.useCallback(
    (command: SlashCommandDefinition) => {
      closeMenu(false);
      setPrompt(`${command.label} `);
      textareaRef.current?.focus();
      requestAnimationFrame(adjustHeight);
    },
    [closeMenu, adjustHeight]
  );

  /** The known command the trimmed prompt starts with, if any. */
  const matchCommand = React.useCallback(
    (text: string): SlashCommandDefinition | undefined => {
      const match = /^\/([\w-]+)(?:\s|$)/.exec(text);
      if (!match) return undefined;
      return availableCommands.find((c) => c.key.toLowerCase() === match[1]!.toLowerCase());
    },
    [availableCommands]
  );

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const next = e.target.value;
    const prev = prompt;
    setPrompt(next);
    adjustHeight();

    if (next === '') setMenuDismissed(false);
    if (menuOpen) {
      // Space after the slash, or no longer slash-prefixed → it's normal text.
      if (!next.startsWith('/') || /\s/.test(next.slice(1))) closeMenu(false);
      else setActiveCommandIndex(0);
    } else if (
      next === '/' &&
      prev === '' &&
      !menuDismissed &&
      !isRunActive &&
      availableCommands.length > 0
    ) {
      setMenuOpen(true);
      setActiveCommandIndex(0);
    }
  };

  const toggleMenuFromButton = () => {
    if (menuOpen) {
      closeMenu(false);
    } else {
      setMenuDismissed(false);
      setMenuOpen(true);
      setActiveCommandIndex(0);
      textareaRef.current?.focus();
    }
  };

  const handleSend = () => {
    const trimmed = prompt.trim();
    if (!trimmed || !isConnected || isRunActive) return;
    // A prompt starting with a known /command sends its key alongside the full
    // text: the server validates + routes deterministically to the command's
    // skill, and any text after the label scopes the request (e.g.
    // "/init-workspace just the storage step"). Commands run in the user's
    // selected mode; 'auto' is resolved per-command server-side from the
    // definition's autoMode.
    const command = matchCommand(trimmed);
    onSend({
      prompt: trimmed,
      model,
      mode,
      ...(command ? { commandKey: command.key } : {}),
    });
    setPrompt('');
    // Reset textarea height after clearing
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (menuOpen) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveCommandIndex((i) => Math.min(i + 1, Math.max(filteredCommands.length - 1, 0)));
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveCommandIndex((i) => Math.max(i - 1, 0));
        return;
      }
      if (e.key === 'Escape') {
        // Keep the typed '/' as plain text; don't re-open until the composer empties.
        e.preventDefault();
        closeMenu(true);
        return;
      }
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        const command = filteredCommands[activeCommandIndex];
        if (command) handleSelectCommand(command);
        else closeMenu(false);
        return;
      }
    }
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const canSend = !isRunActive && isConnected && prompt.trim().length > 0;

  // [Context budget] Usage chip + compact affordance. The Compact button is
  // pure data - it sends the /compact command exactly like typing it.
  const compactCommand = availableCommands.find((c) => c.key === 'compact');
  const usagePct = contextUsageRatio != null ? Math.round(contextUsageRatio * 100) : null;
  const nearContextLimit =
    contextUsageRatio != null && contextUsageRatio >= CONTEXT_COMPACT_SUGGEST_RATIO;
  const handleCompact = () => {
    if (!compactCommand || isRunActive || !isConnected) return;
    onSend({ prompt: compactCommand.label, commandKey: compactCommand.key, model, mode });
  };

  return (
    <div className="ai-chat-composer">
      <SelectedContextBar
        items={explicitContextItems}
        onRemove={onRemoveExplicitContext}
        onClearAll={onClearExplicitContext}
        draftItem={draftExplicitItem}
        onConfirmDraft={onConfirmDraftExplicitContext}
        onClearDraft={onClearDraftExplicitContext}
      />

      <Popover.Root open={menuOpen} onOpenChange={(o) => !o && closeMenu(false)}>
        <div className="ai-chat-composer__input-wrap">
          <textarea
            ref={textareaRef}
            className="ai-chat-composer__textarea"
            placeholder={
              promptPending
                ? 'Answer the question above…'
                : isRunActive
                  ? // A run in flight survives a brief reconnect (server keeps it alive), so keep
                    // reassuring "Running…" rather than flipping to an alarming "Connecting…".
                    'Running… press Stop to cancel'
                  : !isConnected
                    ? 'Connecting…'
                    : 'Ask anything…'
            }
            value={prompt}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            rows={1}
            aria-label="Chat prompt"
            disabled={!isConnected}
          />
        </div>

        <div className="ai-chat-composer__toolbar">
          {/* Model selector - card-style picker */}
          <ComposerPicker
            value={model}
            options={modelOptions}
            onChange={(v) => setModel(v)}
            disabled={isRunActive || availableModels.length === 0}
            ariaLabel="Model"
            placeholder="Model"
          />

          {/* Ask / Agent / Auto - card-style picker. Agent/Auto are alpha-gated, so
              uat/prod render Ask only - still show the picker so the current mode (Ask)
              stays visible; only hide it if no mode is available at all. */}
          {modeOptions.length > 0 ? (
            <ComposerPicker
              value={mode}
              options={modeOptions}
              onChange={handleModeChange}
              disabled={isRunActive}
              ariaLabel="Chat mode"
            />
          ) : null}

          {/* Slash commands - the menu anchors to this button; hidden entirely
              when no command is available to this user. Enabled only while the
              composer is empty: a command must start the message, so once text
              exists the button cannot apply. */}
          {availableCommands.length > 0 ? (
            <Tooltip.Simple label="Commands">
              <Popover.Anchor asChild>
                <button
                  type="button"
                  className="ai-chat-composer__slash-btn"
                  onClick={toggleMenuFromButton}
                  disabled={isRunActive || !isConnected || prompt.trim().length > 0}
                  aria-pressed={menuOpen}
                  aria-label="Slash commands"
                >
                  /
                </button>
              </Popover.Anchor>
            </Tooltip.Simple>
          ) : null}

          {/* "How AI works here" - trust disclosure popover (model, data
              boundary, verification, learning, audit). Own Popover.Root, so it
              never interacts with the slash menu's Root wrapping this toolbar. */}
          <AiDisclosurePanel modelLabel={selectedModelLabel} />

          {/* [Context budget] usage of the model context window, shown as a
              color-coded donut. Hidden below CONTEXT_DONUT_MIN_RATIO (an almost
              empty window isn't worth the noise); warns from
              CONTEXT_COMPACT_SUGGEST_RATIO and offers one-click /compact. The
              conversation auto-compacts at 100% so it never overflows. */}
          {usagePct != null && contextUsageRatio! >= CONTEXT_DONUT_MIN_RATIO ? (
            <Tooltip.Simple
              label={
                nearContextLimit
                  ? `Context window ${usagePct}% full - compact to free space (auto-compacts at 100%)`
                  : `Context window ${usagePct}% full - auto-compacts at 100%`
              }
            >
              <span className="ai-chat-composer__context-usage" role="status">
                <UsageDonut
                  ratio={contextUsageRatio!}
                  warnAt={CONTEXT_COMPACT_SUGGEST_RATIO}
                  dangerAt={CONTEXT_DANGER_RATIO}
                  ariaLabel={`Context window ${usagePct}% full`}
                />
              </span>
            </Tooltip.Simple>
          ) : null}
          {nearContextLimit && compactCommand ? (
            <Tooltip.Simple label={compactCommand.description}>
              <button
                type="button"
                className="ai-chat-btn ai-chat-btn--ghost ai-chat-btn--sm"
                onClick={handleCompact}
                disabled={isRunActive || !isConnected}
              >
                Compact
              </button>
            </Tooltip.Simple>
          ) : null}

          {/* Spacer - doubles as the keyboard-hint slot while typing */}
          <span className="ai-chat-composer__spacer">
            {focused && !isRunActive ? (
              <span className="ai-chat-composer__hint" aria-hidden="true">
                {enterKey} to send · Shift+{enterKey} for a new line
              </span>
            ) : null}
          </span>

          {/* Inline reconnect status - same mechanism as the pane header
            (auto-retry 5×, then a manual Reconnect button), left of Send. */}
          {connectionStatus && onReconnect ? (
            <ChatConnectionIndicator
              connectionStatus={connectionStatus}
              reconnectAttempt={reconnectAttempt ?? 0}
              onReconnect={onReconnect}
              isRunActive={isRunActive}
            />
          ) : null}

          {/* Stop / Send button */}
          {isRunActive ? (
            <button
              type="button"
              className="ai-chat-btn ai-chat-btn--stop"
              onClick={onStop}
              aria-label="Stop run"
            >
              Stop
            </button>
          ) : (
            <button
              type="button"
              className="ai-chat-btn ai-chat-btn--send"
              onClick={handleSend}
              disabled={!canSend}
              aria-label="Send"
            >
              Send
            </button>
          )}
        </div>

        {menuOpen ? (
          <Popover.Portal>
            <Popover.Content
              className="ai-chat-slash-menu__popover"
              side="top"
              align="start"
              sideOffset={6}
              collisionPadding={8}
              // The textarea keeps focus and owns ↑/↓/Enter/Esc - never steal it.
              onOpenAutoFocus={(e) => e.preventDefault()}
              onCloseAutoFocus={(e) => e.preventDefault()}
            >
              <SlashCommandMenu
                commands={filteredCommands}
                activeIndex={activeCommandIndex}
                onSelect={handleSelectCommand}
                onHover={setActiveCommandIndex}
              />
            </Popover.Content>
          </Popover.Portal>
        ) : null}
      </Popover.Root>
    </div>
  );
}
